import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CategoryApi } from '../../api/CategoryApi';
import { AppState } from '../AppStore';
import { getMasterViewWithFlag } from '../../services/CategoryHelper';

import type { IActionPayload } from '../../../types/IApiTypes';
import type { IFeature, IMasterView, IProductType, ISection } from '../../../types';
import type { ICategoryState } from '../../../types/IComponentTypes';
import { IPagination } from '../../../types/IRequestTypes';

// It's perform the role to get the masterview from API
export const getMasterView = createAsyncThunk(
  'category/getMasterView',
  async (params: IActionPayload) => {
    const response = await CategoryApi.getMaster(params);
    return response;
  }
);

// It's perform the role to get the feature family list from API
export const getFeatureFamilyList = createAsyncThunk(
  'category/getFeatureFamilyList',
  async (params: IProductType) => {
    const response = await CategoryApi.getFeatureFamilies(params);
    return response;
  }
);

// It's perform the role to get the category list from API
export const getCategoryList = createAsyncThunk(
  'category/getCategoryList',
  async (action: IPagination) => {
    const response = await CategoryApi.getCategories(action);
    return response;
  }
);

// It's perform the role to save the master view to the API
export const saveMaster = createAsyncThunk(
  'category/saveMaster',
  async (masterView: IMasterView) => {
    const response = await CategoryApi.saveMaster(masterView);
    return response;
  }
);

// Define the initial state using that type
const initialValueForSection = {
  id: '',
  name: '',
  contents: [],
  sections: [],
  hideGeneral: false,
  action: '',
};
const initialState: ICategoryState = {
  masterView: {
    productCode: '',
    sections: [],
  },
  categories: [],
  featureFamily: [],
  assigned: initialValueForSection,
  unassigned: [],
  showAddCategoryDialog: false,
  sectionForAddSubSection: null,
  parentForAddSubSection: null,
  isCategoryDataModified: false,
};

export const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    resetToInitialState: () => {
      return initialState;
    },
    updateMasterView: (state: ICategoryState, action: PayloadAction<IMasterView>) => {
      const { payload } = action;
      state.masterView = payload;
    },

    showAddCategoryDialog: (
      state: ICategoryState,
      action: PayloadAction<{ section?: ISection; flag?: boolean; parent?: ISection | null }>
    ) => {
      const { payload } = action;
      state.sectionForAddSubSection = payload.section ?? null;
      state.parentForAddSubSection = payload.parent ?? null;
      state.showAddCategoryDialog = payload.flag ?? true;
    },
    addCategory: (state: ICategoryState, action: PayloadAction<Array<ISection>>) => {
      const data = action.payload;
      const { masterView } = state;
      masterView.sections = data;
      state.masterView = masterView;
      state.isCategoryDataModified = true;
    },
    updateCategoryModify: (state: ICategoryState) => {
      state.isCategoryDataModified = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET MASTER
      .addCase(getMasterView.fulfilled, (state: ICategoryState, action) => {
        if (action?.payload?.data?.productCode) {
          const masterViewData = getMasterViewWithFlag(
            action.payload.data,
            action.payload.data.sections[0]
          );
          if (masterViewData != null) {
            state.masterView = masterViewData;
          }
        } else {
          state.masterView = initialState.masterView;
        }

        state.assigned = initialValueForSection;
        state.isCategoryDataModified = false;
      })

      // GET FAMILY FEATURE
      .addCase(getFeatureFamilyList.fulfilled, (state: ICategoryState, action) => {
        if (action.payload.data !== undefined && action.payload.data.length > 0) {
          action.payload.data = action.payload.data.filter(
            (feature: IFeature) => feature.code !== 'PMP'
          );
          state.featureFamily = action.payload.data;
        } else {
          state.featureFamily = [];
        }
      })

      // GET CATEGORY
      .addCase(getCategoryList.fulfilled, (state: ICategoryState, action) => {
        if (action.payload.data !== undefined && action.payload.data.length > 0) {
          state.categories = action.payload.data;
        } else {
          state.categories = [];
        }
      })

      // SAVE CATEGORY
      .addCase(saveMaster.fulfilled, (state: ICategoryState, action) => {
        if (action?.payload?.productCode) {
          state.masterView = action.payload;
          state.isCategoryDataModified = false;
        }
      });
  },
});

export const {
  updateMasterView,
  showAddCategoryDialog,
  addCategory,
  resetToInitialState,
  updateCategoryModify,
} = categorySlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectCategories = (state: AppState) => state.category;

export default categorySlice.reducer;
